import type { CanHaveCrop } from '../../items/shared';

export type LayoutOption = 'Fill' | 'Fit';

export type LayoutRect = {
  width: number;
  height: number;
  left: number;
  top: number;
};

/**
 * 비디오 레이아웃(채우기/맞춤)별로 필요한 아이템 치수를 계산한다.
 *
 * crop 기준:
 * - crop === null → 원본 아이템 전체를 컴포지션에 맞춘다(크롭 모드에서 원본을 편집할 때).
 * - crop 있음 → 보이는(크롭된) 영역을 컴포지션에 맞추고, 그 결과가 나오도록 아이템 박스를
 *   역산한다(크롭 버튼 누르기 전, 화면엔 크롭된 결과만 보일 때). crop 분수가 0이면 전체
 *   기준과 동일해진다.
 */
export const computeLayoutRect = ({
  layout,
  assetWidth,
  assetHeight,
  compositionWidth,
  compositionHeight,
  crop,
}: {
  layout: LayoutOption;
  assetWidth: number;
  assetHeight: number;
  compositionWidth: number;
  compositionHeight: number;
  crop: CanHaveCrop | null;
}): LayoutRect => {
  const safeAssetWidth = assetWidth > 0 ? assetWidth : compositionWidth;
  const safeAssetHeight = assetHeight > 0 ? assetHeight : compositionHeight;
  const safeCompositionWidth = Math.max(1, compositionWidth);
  const safeCompositionHeight = Math.max(1, compositionHeight);

  const cropLeft = crop?.cropLeft ?? 0;
  const cropTop = crop?.cropTop ?? 0;
  const cropRight = crop?.cropRight ?? 0;
  const cropBottom = crop?.cropBottom ?? 0;
  const visibleFractionW = Math.max(1e-6, 1 - cropLeft - cropRight);
  const visibleFractionH = Math.max(1e-6, 1 - cropTop - cropBottom);

  // 컴포지션에 맞출 콘텐츠의 원본 픽셀 크기 — crop 있으면 보이는 영역, 없으면 전체.
  const contentWidth = crop
    ? safeAssetWidth * visibleFractionW
    : safeAssetWidth;
  const contentHeight = crop
    ? safeAssetHeight * visibleFractionH
    : safeAssetHeight;

  // 원본 비율을 유지한 채로 컴포지션에 맞도록 확대/축소 비율을 구한다
  const widthScale = safeCompositionWidth / contentWidth;
  const heightScale = safeCompositionHeight / contentHeight;
  const scale =
    layout === 'Fit'
      ? Math.min(widthScale, heightScale)
      : Math.max(widthScale, heightScale);

  // 화면에 보이는 콘텐츠(= 크롭된 결과 또는 전체 아이템)의 목표 치수·중앙 좌표
  const rawWidth = contentWidth * scale;
  const rawHeight = contentHeight * scale;
  const visibleWidth =
    layout === 'Fill'
      ? Math.max(safeCompositionWidth, Math.ceil(rawWidth))
      : Math.max(1, Math.round(rawWidth));
  const visibleHeight =
    layout === 'Fill'
      ? Math.max(safeCompositionHeight, Math.ceil(rawHeight))
      : Math.max(1, Math.round(rawHeight));
  const visibleLeft = Math.round((safeCompositionWidth - visibleWidth) / 2);
  const visibleTop = Math.round((safeCompositionHeight - visibleHeight) / 2);

  if (!crop) {
    // 전체 아이템 기준: 보이는 목표가 곧 아이템 박스.
    return {
      width: visibleWidth,
      height: visibleHeight,
      left: visibleLeft,
      top: visibleTop,
    };
  }

  // 보이는(크롭) 영역 기준: 위 목표가 getRectAfterCrop 이 되도록 아이템 박스를 역산.
  const width = Math.max(1, Math.round(visibleWidth / visibleFractionW));
  const height = Math.max(1, Math.round(visibleHeight / visibleFractionH));
  const left = Math.round(visibleLeft - cropLeft * width);
  const top = Math.round(visibleTop - cropTop * height);

  return { width, height, left, top };
};
